import _ from "lodash";
import state from "../state/state";
import { transformFromChecklistEntities } from "../utils/transformUtils";
import activityJsonRules from "../utils/activityJsonRules";
import { addEntityMarker, removeEntityMarkers, createErrorMarker, createWarningMarker } from "./editorActions";

// Activity validation in the authoring tool: the rules from docs/activity-json/AUTHORING_GUIDE.md (hierarchy, ids,
// guids, dangling condition references, link targets, options under the right key, runtime keys) run against the
// exported player-checklist JSON of the open activity, and every finding becomes an entity marker under the
// "activityValidation" property of the node's guid. The outline badge (components/editor/validationBadge.js)
// subscribes to those markers; the save pre-check lists the errors; the Tools menu (components/editor/
// authoringToolsMenu.js) shows the summary, runs a pass on demand and switches real-time validation.
//
// Cost: a pass exports the whole activity (about 1.5 s for 5,000 items on a laptop) and validates it (0.25 s), on the
// main thread. Real-time validation therefore pauses itself above LARGE_ACTIVITY_NODE_COUNT nodes unless the author
// turns it on explicitly, and the author can always run it on demand.
export const VALIDATION_MARKER_PROPERTY = "activityValidation";
export const VALIDATION_SUMMARY_PATH = ["editActivityMetaData", "validationSummary"];
export const REALTIME_VALIDATION_PREFERENCE_KEY = "ambifi.authoring.realtimeValidation";
export const REALTIME_PREFERENCES = ["auto", "on", "off"];
export const LARGE_ACTIVITY_NODE_COUNT = 1000;
const DEBOUNCE_MS = 700;
const IDLE_TIMEOUT_MS = 1500;

let validationTimer = null;
let markedGuids = new Set();

// The node a problem path ("$.children[2].children[0].label1") points at, by walking the exported JSON.
export function nodeForProblemPath(activity, path) {
	const indices = Array.from(String(path || "").matchAll(/children\[(\d+)\]/g)).map((match) => Number(match[1]));
	let node = activity;
	for (const index of indices) {
		if (!node || !Array.isArray(node.children) || !node.children[index]) {
			return null;
		}
		node = node.children[index];
	}
	return node || null;
}

export function groupProblemsByGuid(activity, problems) {
	const byGuid = new Map();
	(problems || []).forEach((problem) => {
		const node = nodeForProblemPath(activity, problem.path);
		const guid = node && typeof node.guid === "string" && node.guid ? node.guid : null;
		if (!guid) {
			return;
		}
		if (!byGuid.has(guid)) {
			byGuid.set(guid, []);
		}
		byGuid.get(guid).push(problem);
	});
	return byGuid;
}

// Nodes in the authoring entity tree (root included); cheap enough to run on every debounced pass.
export function countActivityNodes(root) {
	let count = 0;
	const stack = root ? [root] : [];
	while (stack.length) {
		const node = stack.pop();
		count += 1;
		if (Array.isArray(node.children)) {
			for (let index = 0; index < node.children.length; index += 1) {
				if (node.children[index]) {
					stack.push(node.children[index]);
				}
			}
		}
	}
	return count;
}

function readStoredPreference() {
	try {
		const value = typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem(REALTIME_VALIDATION_PREFERENCE_KEY) : null;
		return REALTIME_PREFERENCES.includes(value) ? value : "auto";
	} catch (err) {
		return "auto";
	}
}

export function getRealtimeValidationPreference() {
	return readStoredPreference();
}

export function setRealtimeValidationPreference(value) {
	const next = REALTIME_PREFERENCES.includes(value) ? value : "auto";
	try {
		if (typeof window !== "undefined" && window.localStorage) {
			if (next === "auto") {
				window.localStorage.removeItem(REALTIME_VALIDATION_PREFERENCE_KEY);
			} else {
				window.localStorage.setItem(REALTIME_VALIDATION_PREFERENCE_KEY, next);
			}
		}
	} catch (err) {
		// Storage can be unavailable (private mode, blocked); the preference then lasts for the session's default.
	}
	return next;
}

// Whether real-time validation runs for an activity of nodeCount nodes under a preference.
export function resolveRealtimeValidation(nodeCount, preference = getRealtimeValidationPreference()) {
	if (preference === "on") {
		return { active: true, reason: "on", nodeCount, preference };
	}
	if (preference === "off") {
		return { active: false, reason: "off", nodeCount, preference };
	}
	if (nodeCount > LARGE_ACTIVITY_NODE_COUNT) {
		return { active: false, reason: "auto-large", nodeCount, preference: "auto" };
	}
	return { active: true, reason: "auto", nodeCount, preference: "auto" };
}

function currentRealtimeDecision() {
	return resolveRealtimeValidation(countActivityNodes(state.get(["tree", "root"])));
}

export function exportOpenActivity() {
	const root = state.get(["tree", "root"]);
	if (!root || !root.entity) {
		return null;
	}
	try {
		return transformFromChecklistEntities(_.cloneDeep(root));
	} catch (err) {
		console.warn("Activity validation: export failed", err);
		return null;
	}
}

export function validateOpenActivity() {
	const activity = exportOpenActivity();
	if (!activity) {
		return { activity: null, problems: [] };
	}
	return { activity, problems: activityJsonRules.validateActivity(activity) };
}

export function clearActivityValidationMarkers() {
	markedGuids.forEach((guid) => removeEntityMarkers(guid, VALIDATION_MARKER_PROPERTY));
	markedGuids = new Set();
	state.set(VALIDATION_SUMMARY_PATH, null);
}

function now() {
	return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}

function markValidationStale(decision) {
	const previous = state.get(VALIDATION_SUMMARY_PATH) || {};
	state.set(VALIDATION_SUMMARY_PATH, { ...previous, stale: true, realtime: false, reason: decision.reason, preference: decision.preference, nodeCount: decision.nodeCount });
}

// Runs a full pass now. `trigger` is "realtime" or "manual"; the summary records it so the menu can explain itself.
export function runActivityValidation({ trigger = "manual" } = {}) {
	if (validationTimer) {
		clearTimeout(validationTimer);
		validationTimer = null;
	}
	const started = now();
	const { activity, problems } = validateOpenActivity();
	if (!activity) {
		clearActivityValidationMarkers();
		return null;
	}
	const byGuid = groupProblemsByGuid(activity, problems);
	markedGuids.forEach((guid) => {
		if (!byGuid.has(guid)) {
			removeEntityMarkers(guid, VALIDATION_MARKER_PROPERTY);
		}
	});
	byGuid.forEach((list, guid) => {
		removeEntityMarkers(guid, VALIDATION_MARKER_PROPERTY);
		list.forEach((problem) => {
			const make = problem.level === "error" ? createErrorMarker : createWarningMarker;
			addEntityMarker(guid, make(problem.message, `${problem.path}|${problem.message}`, { path: problem.path }), VALIDATION_MARKER_PROPERTY);
		});
	});
	markedGuids = new Set(byGuid.keys());
	const errors = problems.filter((problem) => problem.level === "error").length;
	const decision = currentRealtimeDecision();
	const summary = {
		errors,
		warnings: problems.length - errors,
		nodes: byGuid.size,
		at: Date.now(),
		durationMs: Math.round(now() - started),
		trigger,
		stale: false,
		realtime: decision.active,
		reason: decision.reason,
		preference: decision.preference,
		nodeCount: decision.nodeCount
	};
	state.set(VALIDATION_SUMMARY_PATH, summary);
	return summary;
}

function runWhenIdle(fn) {
	if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
		window.requestIdleCallback(() => fn(), { timeout: IDLE_TIMEOUT_MS });
	} else {
		fn();
	}
}

export function scheduleActivityValidation(delay = DEBOUNCE_MS) {
	if (validationTimer) {
		clearTimeout(validationTimer);
	}
	validationTimer = setTimeout(() => {
		validationTimer = null;
		const decision = currentRealtimeDecision();
		if (!decision.active) {
			markValidationStale(decision);
			return;
		}
		runWhenIdle(() => runActivityValidation({ trigger: "realtime" }));
	}, delay);
}

// Current summary plus the live real-time decision, for the Tools menu.
export function getValidationStatus() {
	return { summary: state.get(VALIDATION_SUMMARY_PATH) || null, ...currentRealtimeDecision() };
}

// True when a Baobab update event touched the authoring tree (not selection or expand/collapse churn).
export function updateTouchesActivityTree(event) {
	const transactions = _.get(event, ["data", "transaction"]);
	if (!Array.isArray(transactions)) {
		return false;
	}
	return transactions.some((transaction) => {
		const path = transaction && transaction.path;
		if (!Array.isArray(path) || path[0] !== "tree") {
			return false;
		}
		const last = path[path.length - 1];
		return last !== "selected" && last !== "expanded" && last !== "editing";
	});
}

let listening = false;
const onStateUpdate = (event) => {
	if (updateTouchesActivityTree(event)) {
		scheduleActivityValidation();
	}
};

export function startActivityValidationListener() {
	if (listening) {
		return;
	}
	listening = true;
	state.on("update", onStateUpdate);
	scheduleActivityValidation(0);
}

export function stopActivityValidationListener() {
	if (!listening) {
		return;
	}
	listening = false;
	state.off("update", onStateUpdate);
	if (validationTimer) {
		clearTimeout(validationTimer);
		validationTimer = null;
	}
}
